<script setup lang="ts">
import { computed, nextTick, reactive, ref, watch, type ComponentPublicInstance } from 'vue'
import { Trash2 } from 'lucide-vue-next'
import { VButton } from '@/modules/shared/components'
import { useNotificationStore } from '@/modules/shared/stores/notification-store'
import { useBuilderStore } from '../stores/builder-store'
import type { ApplicationQuestion, QuestionType } from '../types'

const props = defineProps<{ question: ApplicationQuestion; autofocus?: boolean }>()
const emit = defineEmits<{ focused: [] }>()
const builderStore = useBuilderStore()
const notificationStore = useNotificationStore()

const typeLabels: Record<QuestionType, string> = {
  short_answer: 'Short Answer',
  multiple_choice: 'Multiple Choice',
  boolean: 'Yes / No',
  file_upload: 'File Upload',
  matrix_table: 'Matrix Table',
}

///////////////////////////////////////////////////////////////////////////////
// Question name / description — always-visible inline fields. Nothing saves
// on blur: edits persist only through the explicit Save button (or Enter in
// the name field). The legacy forms/_form.html.erb submit_on_change behavior
// was dropped (tickets/3065): auto-save had no undo and risked data loss.
/////////////////////////////////////////////////////////////////////////////

const nameInput = ref<HTMLInputElement | null>(null)
const name = ref(props.question.name)
const description = ref(props.question.description ?? '')
const saving = ref(false)

// a refetch (any card's save, a sort, a choice edit…) replaces every question
// object — adopt incoming values only for fields the user has not edited
// since, so unsaved changes in one card survive refreshes from another
let baseName = props.question.name
let baseDescription = props.question.description ?? ''

// choice rows follow the same rule: local drafts hold every edit until Save
// is clicked, so blurring a choice field never persists anything
const choiceDrafts = reactive(new Map<number, { value: number; description: string }>())
for (const choice of props.question.choices) {
  choiceDrafts.set(choice.id, { value: choice.value, description: choice.description })
}

watch(
  () => props.question,
  (question, previous) => {
    if (name.value === baseName) {
      name.value = question.name
      baseName = question.name
    }
    if (description.value === baseDescription) {
      description.value = question.description ?? ''
      baseDescription = question.description ?? ''
    }

    // seed drafts for rows that appeared, adopt incoming server values only
    // for drafts the user has not edited since, drop rows deleted elsewhere
    const previousChoices = new Map((previous?.choices ?? []).map((choice) => [choice.id, choice]))
    for (const choice of question.choices) {
      const draft = choiceDrafts.get(choice.id)
      const before = previousChoices.get(choice.id)
      if (!draft) {
        choiceDrafts.set(choice.id, { value: choice.value, description: choice.description })
      } else if (before && draft.value === before.value && draft.description === before.description) {
        draft.value = choice.value
        draft.description = choice.description
      }
    }
    for (const id of choiceDrafts.keys()) {
      if (!question.choices.some((choice) => choice.id === id)) choiceDrafts.delete(id)
    }
  },
)

// the page flags a freshly created question so its name field gets focus
watch(
  () => props.autofocus,
  async (on) => {
    if (!on) return

    await nextTick()
    nameInput.value?.focus()
    emit('focused')
  },
  { immediate: true },
)

const hasChanges = computed(
  () =>
    name.value.trim() !== props.question.name ||
    description.value !== (props.question.description ?? '') ||
    props.question.choices.some((choice) => {
      const draft = choiceDrafts.get(choice.id)
      return draft !== undefined && (draft.description !== choice.description || draft.value !== choice.value)
    }),
)

const save = async () => {
  if (saving.value || !hasChanges.value) return

  const sentName = name.value
  const sentDescription = description.value
  const questionDirty =
    sentName.trim() !== props.question.name || sentDescription !== (props.question.description ?? '')
  const sentChoices = props.question.choices
    .filter((choice) => {
      const draft = choiceDrafts.get(choice.id)
      return draft !== undefined && (draft.description !== choice.description || draft.value !== choice.value)
    })
    .map((choice) => ({ ...choiceDrafts.get(choice.id)!, id: choice.id }))

  saving.value = true
  try {
    if (questionDirty) {
      await builderStore.updateQuestion(props.question.id, sentName.trim() || props.question.name, sentDescription)
      // what was sent becomes the baseline: fields the user re-edited during
      // the request keep their newer text; untouched ones adopt the refetch
      baseName = sentName
      baseDescription = sentDescription
      // legacy update.js.erb enqueued 'Question was successfully saved.' — only
      // after the request actually succeeded, never while pending or failed
      notificationStore.enqueue('success', 'Question was successfully saved.')
    }
    for (const sent of sentChoices) {
      await builderStore.updateChoice(sent.id, sent.description, sent.value)
      notificationStore.enqueue('success', 'Choice was successfully saved.')
    }
  } catch {
    // error already surfaced via { silent: false }; whatever did not get sent
    // stays dirty for the next Save
  } finally {
    saving.value = false
  }
}

const remove = async () => {
  if (
    !window.confirm(
      'Are you sure? This action can not be undone. All companies currently taking this application will still be asked to answer this question.',
    )
  )
    return

  await builderStore.destroyQuestion(props.question.id)
}

///////////////////////////////////////////////////////////////////////////////
// Choices (multiple choice only — boolean Yes/No is fixed). Legacy rows:
// 'Choice Value (Numeric)' + 'Choice Description'; 'Add Choice' creates a
// blank row (value 0) immediately — an explicit click, not auto-save.
//
// Rows render in insertion order (the same order the company taking flow
// presents the choices in). The legacy admin sorted value DESC, which made a
// row leap to the top the moment its numeric value changed — under the user's
// cursor — so that sort is deliberately not carried over.
//
// Fields bind to drafts and persist only through the card's Save button
// (tickets/3065): the legacy save-on-change for rows was removed with the
// rest of auto-save.
///////////////////////////////////////////////////////////////////////////////

const choiceRows = computed(() =>
  [...props.question.choices]
    .sort((a, b) => a.id - b.id)
    .map((choice) => ({ choice, draft: choiceDrafts.get(choice.id)! })),
)
const choiceValueInputs = new Map<number, HTMLInputElement>()
const bindChoiceValueInput = (choiceId: number, el: Element | ComponentPublicInstance | null) => {
  if (el instanceof HTMLInputElement) choiceValueInputs.set(choiceId, el)
  else choiceValueInputs.delete(choiceId)
}
const addingChoice = ref(false)

const addChoice = async () => {
  if (addingChoice.value) return

  addingChoice.value = true
  try {
    const choice = await builderStore.createChoice(props.question.id, '', 0)
    // keep the user where they are: focus the new row's value field in place
    await nextTick()
    choiceValueInputs.get(choice.id)?.focus()
  } catch {
    // error already surfaced via { silent: false }
  } finally {
    addingChoice.value = false
  }
}

const removeChoice = async (choiceId: number) => {
  if (
    !window.confirm(
      'Are you sure? This action can not be undone. All companies who have chosen this choice will still see this choice.',
    )
  )
    return

  await builderStore.destroyChoice(choiceId)
}
</script>

<template>
  <div class="rounded-xl border border-gray-200 bg-white shadow-sm">
    <div class="flex flex-wrap items-start gap-3 border-b border-gray-100 px-4 py-3">
      <span
        class="mt-0.5 inline-flex h-6 w-8 shrink-0 items-center justify-center rounded bg-[#3b88af] text-xs font-semibold text-white"
      >
        {{ String(question.position).padStart(2, '0') }}
      </span>

      <div class="field-with-bar min-w-[10rem] flex-1">
        <label class="mb-1 block text-xs font-medium text-gray-500">Question Name</label>
        <input
          ref="nameInput"
          v-model="name"
          type="text"
          class="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium outline-none focus:border-[#3b88af]"
          @keyup.enter="save"
        />
        <span class="bar" aria-hidden="true"></span>
      </div>

      <div class="flex shrink-0 items-center gap-2 pt-4">
        <span class="rounded bg-gray-100 px-2 py-1 text-xs text-gray-600">{{ typeLabels[question.type] }}</span>
        <VButton
          variant="v-blue"
          size="xs"
          class="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2"
          :disabled="!hasChanges"
          :loading="saving"
          @click="save"
        >
          Save
        </VButton>
        <button class="text-sm text-red-400 hover:text-red-600" title="Delete question" @click="remove">Delete</button>
      </div>
    </div>

    <div class="px-4 py-3">
      <div class="field-with-bar">
        <label class="mb-1 block text-xs font-medium text-gray-500">Question Description</label>
        <textarea
          v-model="description"
          rows="3"
          class="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm outline-none focus:border-[#3b88af]"
          placeholder="Add a description for this question (optional)"
        />
        <span class="bar" aria-hidden="true"></span>
      </div>

      <!-- choices (multiple choice only — legacy forms/_form_multiple_choice.html.erb) -->
      <div v-if="question.type === 'multiple_choice'" class="mt-4">
        <ul class="flex flex-col gap-3">
          <li
            v-for="row in choiceRows"
            :key="row.choice.id"
            class="grid grid-cols-[11rem_1fr_auto] items-start gap-x-3"
          >
            <div class="field-with-bar">
              <label class="mb-1 block whitespace-nowrap text-xs font-medium text-gray-500"
                >Choice Value (Numeric)</label
              >
              <input
                :ref="(el) => bindChoiceValueInput(row.choice.id, el)"
                :value="row.draft.value"
                type="number"
                class="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm outline-none focus:border-[#3b88af]"
                @input="
                  row.draft.value =
                    ($event.target as HTMLInputElement).value === ''
                      ? 0
                      : Number(($event.target as HTMLInputElement).value)
                "
              />
              <span class="bar" aria-hidden="true"></span>
            </div>
            <div class="field-with-bar">
              <label class="mb-1 block whitespace-nowrap text-xs font-medium text-gray-500">Choice Description</label>
              <input
                :value="row.draft.description"
                type="text"
                class="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm outline-none focus:border-[#3b88af]"
                @input="row.draft.description = ($event.target as HTMLInputElement).value"
              />
              <span class="bar" aria-hidden="true"></span>
            </div>
            <button
              class="mt-[30px] shrink-0 text-gray-400 hover:text-red-600"
              title="Delete choice"
              aria-label="Delete choice"
              @click="removeChoice(row.choice.id)"
            >
              <Trash2 class="h-4 w-4" />
            </button>
          </li>
        </ul>

        <div class="mt-3 text-center">
          <button class="text-sm text-[#3b88af] hover:underline" :disabled="addingChoice" @click="addChoice">
            Add Choice
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Legacy builder field treatment, ported from the Rails input-with-bar
   helpers (paper_inputs.css.scss): a 2px green underline whose halves expand
   from the center while the field is focused — the editing indicator the
   original page showed on every builder field. */
.field-with-bar .bar {
  position: relative;
  display: block;
  width: 100%;
}

.field-with-bar .bar::before,
.field-with-bar .bar::after {
  content: '';
  position: absolute;
  bottom: 0;
  height: 2px;
  width: 0;
  background: #008d52; /* $true-green */
  transition: 0.2s ease all;
}

.field-with-bar .bar::before {
  left: 50%;
}

.field-with-bar .bar::after {
  right: 50%;
}

.field-with-bar:focus-within .bar::before,
.field-with-bar:focus-within .bar::after {
  width: 50%;
}
</style>
